import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { usersApi, userSkillsApi, skillMastersApi, SkillCategoryNames, SkillCategory } from '../services/api';
import UserCvSection from '../components/UserCvSection';
import './UserProfile.css';

function UserProfile({ currentUser }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingSkill, setEditingSkill] = useState(null);
  const [availableSkills, setAvailableSkills] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    skillId: '',
    proficiencyLevel: 3,
    yearsOfExperience: 0,
    lastUsed: '',
    personalNote: ''
  });

  useEffect(() => {
    fetchUserData();
  }, [id]);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const userData = await usersApi.getById(id);
      setUser(userData);

      // Fetch user's skills using new API
      try {
        const skillsData = await userSkillsApi.getUserSkills(id);
        setSkills(skillsData);
      } catch (skillError) {
        console.log('Could not fetch skills:', skillError);
        setSkills([]);
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching user:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableSkills = async () => {
    try {
      const allSkills = await skillMastersApi.getAll();
      // Filter out skills the user already has
      const userSkillIds = skills.map(s => s.skillId);
      const available = allSkills.filter(skill => !userSkillIds.includes(skill.id));
      setAvailableSkills(available);
    } catch (err) {
      console.error('Error fetching available skills:', err);
      setAvailableSkills([]);
    }
  };

  const handleAddSkill = async (e) => {
    e.preventDefault();
    try {
      await userSkillsApi.assignSkill({
        userId: id,
        skillId: formData.skillId,
        proficiencyLevel: parseInt(formData.proficiencyLevel),
        yearsOfExperience: parseFloat(formData.yearsOfExperience) || 0,
        lastUsed: formData.lastUsed || null,
        personalNote: formData.personalNote || null
      });
      setShowAddModal(false);
      setFormData({
        skillId: '',
        proficiencyLevel: 3,
        yearsOfExperience: 0,
        lastUsed: '',
        personalNote: ''
      });
      fetchUserData();
    } catch (err) {
      alert(err.message);
      console.error('Error adding skill:', err);
    }
  };

  const handleEditSkill = async (e) => {
    e.preventDefault();
    try {
      await userSkillsApi.update(editingSkill.id, {
        proficiencyLevel: parseInt(formData.proficiencyLevel),
        yearsOfExperience: parseFloat(formData.yearsOfExperience) || 0,
        lastUsed: formData.lastUsed || null,
        personalNote: formData.personalNote || null
      });
      setShowEditModal(false);
      setEditingSkill(null);
      setFormData({
        skillId: '',
        proficiencyLevel: 3,
        yearsOfExperience: 0,
        lastUsed: '',
        personalNote: ''
      });
      fetchUserData();
    } catch (err) {
      alert(err.message);
      console.error('Error updating skill:', err);
    }
  };

  const handleDeleteSkill = async (skillId, skillName) => {
    if (window.confirm(`Remove skill "${skillName}" from this user?`)) {
      try {
        await userSkillsApi.delete(skillId);
        fetchUserData();
      } catch (err) {
        alert(err.message);
        console.error('Error removing skill:', err);
      }
    }
  };

  const openAddModal = () => {
    fetchAvailableSkills();
    setShowAddModal(true);
  };

  const openEditModal = (skill) => {
    setEditingSkill(skill);
    setFormData({
      skillId: skill.skillId,
      proficiencyLevel: skill.proficiencyLevel,
      yearsOfExperience: skill.yearsOfExperience,
      lastUsed: skill.lastUsed ? skill.lastUsed.split('T')[0] : '',
      personalNote: skill.personalNote || ''
    });
    setShowEditModal(true);
  };

  const closeModal = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setEditingSkill(null);
    setSearchQuery('');
    setFormData({
      skillId: '',
      proficiencyLevel: 3,
      yearsOfExperience: 0,
      lastUsed: '',
      personalNote: ''
    });
  };

  const getInitials = (firstName, lastName) => {
    if (!firstName && !lastName) return '?';
    const firstInitial = firstName ? firstName[0] : '';
    const lastInitial = lastName ? lastName[0] : '';
    return (firstInitial + lastInitial).toUpperCase();
  };

  const groupSkillsByCategory = () => {
    const professionalCategories = ['Technical', 'SoftSkills', 'Soft Skills', 'DomainKnowledge', 'Domain Knowledge', 'Tools', 'Languages', 'Certifications'];
    const professional = skills.filter(s => professionalCategories.includes(s.category.replace(/\s+/g, '')));
    const personal = skills.filter(s => !professionalCategories.includes(s.category.replace(/\s+/g, '')));
    return { professional, personal };
  };

  const renderSkillsList = (skillsList, title) => {
    if (skillsList.length === 0) return null;

    return (
      <div className="skills-section">
        <h3>{title}</h3>
        <div className="skills-grid">
          {skillsList.map(skill => (
            <div key={skill.id} className="skill-card">
              <div className="skill-header">
                <h4>{skill.skillName}</h4>
                <div className="skill-actions">
                  <button
                    className="btn-icon"
                    onClick={() => openEditModal(skill)}
                    title="Edit"
                  >
                    ✏️
                  </button>
                  <button
                    className="btn-icon"
                    onClick={() => handleDeleteSkill(skill.id, skill.skillName)}
                    title="Remove"
                  >
                    🗑️
                  </button>
                </div>
              </div>
              <span className="skill-category">{skill.category}</span>
              {skill.personalNote && (
                <p className="skill-description">{skill.personalNote}</p>
              )}
              <div className="skill-details">
                <div className="skill-detail">
                  <span className="label">Proficiency:</span>
                  <div className="proficiency-bar">
                    <div
                      className="proficiency-fill"
                      style={{ width: `${(skill.proficiencyLevel / 5) * 100}%` }}
                    />
                  </div>
                  <span className="proficiency-text">{skill.proficiencyLevel}/5</span>
                </div>
                {skill.yearsOfExperience > 0 && (
                  <div className="skill-detail">
                    <span className="label">Experience:</span>
                    <span className="value">{skill.yearsOfExperience} years</span>
                  </div>
                )}
                {skill.lastUsed && (
                  <div className="skill-detail">
                    <span className="label">Last Used:</span>
                    <span className="value">{new Date(skill.lastUsed).toLocaleDateString()}</span>
                  </div>
                )}
                {skill.endorsementCount > 0 && (
                  <div className="skill-detail">
                    <span className="label">Endorsements:</span>
                    <span className="value">{skill.endorsementCount}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="user-profile-container">
        <div className="loading">Loading user profile...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="user-profile-container">
        <div className="error-message">Error: {error}</div>
        <button onClick={() => navigate('/users')} className="btn btn-secondary">
          Back to Users
        </button>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="user-profile-container">
        <div className="error-message">User not found</div>
        <button onClick={() => navigate('/users')} className="btn btn-secondary">
          Back to Users
        </button>
      </div>
    );
  }

  const { professional, personal } = groupSkillsByCategory();

  return (
    <div className="user-profile-container">
      <div className="profile-header">
        <Link to="/users" className="back-link">
          ← Back to Users
        </Link>
      </div>

      <div className="profile-card">
        <div className="profile-main">
          <div className="avatar-large">
            {getInitials(user.firstName, user.lastName)}
          </div>
          <div className="profile-info">
            <h1>{user.firstName} {user.lastName}</h1>
            <p className="user-email">{user.emailAddress}</p>
            {user.role && (
              <div className="user-meta">
                <span className="meta-item">
                  <strong>Role:</strong> {user.role}
                </span>
              </div>
            )}
            {user.status && (
              <span className={`badge status-${user.status.toLowerCase()}`}>
                {user.status}
              </span>
            )}
          </div>
        </div>

        <div className="profile-details">
          <div className="detail-row">
            <span className="detail-label">Last Sign In:</span>
            <span className="detail-value">
              {new Date(user.lastSignedIn).toLocaleString()}
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Joined:</span>
            <span className="detail-value">
              {new Date(user.createdTime).toLocaleDateString()}
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Last Updated:</span>
            <span className="detail-value">
              {new Date(user.updatedTime).toLocaleDateString()}
            </span>
          </div>
        </div>
      </div>

      <div className="skills-header">
        <h2>Skills</h2>
        <button className="btn btn-primary" onClick={openAddModal}>
          + Add Skill
        </button>
      </div>

      {renderSkillsList(professional, 'Professional Skills')}
      {renderSkillsList(personal, 'Personal Interests & Hobbies')}

      {skills.length === 0 && (
        <div className="empty-skills">
          <p>No skills added yet</p>
        </div>
      )}

      {/* CV Section */}
      <UserCvSection userId={id} />

      {/* Add Skill Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Skill</h2>
              <button className="close-button" onClick={closeModal}>×</button>
            </div>
            <form onSubmit={handleAddSkill}>
              <div className="form-group">
                <label htmlFor="skillId">Skill *</label>
                <input
                  type="text"
                  placeholder="Search skills..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="form-input"
                  style={{ marginBottom: '10px' }}
                />
                <select
                  id="skillId"
                  value={formData.skillId}
                  onChange={(e) => setFormData({ ...formData, skillId: e.target.value })}
                  required
                  className="form-input"
                  size="8"
                >
                  <option value="">Select a skill...</option>
                  {availableSkills
                    .filter(skill =>
                      skill.name.toLowerCase().includes(searchQuery.toLowerCase())
                    )
                    .map(skill => (
                      <option key={skill.id} value={skill.id}>
                        {skill.name} ({skill.category})
                      </option>
                    ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="proficiencyLevel">Proficiency Level * (1-5)</label>
                <input
                  type="range"
                  id="proficiencyLevel"
                  min="1"
                  max="5"
                  value={formData.proficiencyLevel}
                  onChange={(e) => setFormData({ ...formData, proficiencyLevel: e.target.value })}
                  className="form-input"
                />
                <div className="range-value">Level: {formData.proficiencyLevel}/5</div>
              </div>

              <div className="form-group">
                <label htmlFor="yearsOfExperience">Years of Experience</label>
                <input
                  type="number"
                  id="yearsOfExperience"
                  min="0"
                  step="0.5"
                  value={formData.yearsOfExperience}
                  onChange={(e) => setFormData({ ...formData, yearsOfExperience: e.target.value })}
                  className="form-input"
                  placeholder="0"
                />
              </div>

              <div className="form-group">
                <label htmlFor="lastUsed">Last Used</label>
                <input
                  type="date"
                  id="lastUsed"
                  value={formData.lastUsed}
                  onChange={(e) => setFormData({ ...formData, lastUsed: e.target.value })}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="personalNote">Personal Note</label>
                <textarea
                  id="personalNote"
                  value={formData.personalNote}
                  onChange={(e) => setFormData({ ...formData, personalNote: e.target.value })}
                  className="form-input"
                  rows="3"
                  placeholder="Add a personal note about this skill..."
                />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Add Skill
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Skill Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Skill: {editingSkill?.skillName}</h2>
              <button className="close-button" onClick={closeModal}>×</button>
            </div>
            <form onSubmit={handleEditSkill}>
              <div className="form-group">
                <label htmlFor="edit-proficiencyLevel">Proficiency Level * (1-5)</label>
                <input
                  type="range"
                  id="edit-proficiencyLevel"
                  min="1"
                  max="5"
                  value={formData.proficiencyLevel}
                  onChange={(e) => setFormData({ ...formData, proficiencyLevel: e.target.value })}
                  className="form-input"
                />
                <div className="range-value">Level: {formData.proficiencyLevel}/5</div>
              </div>

              <div className="form-group">
                <label htmlFor="edit-yearsOfExperience">Years of Experience</label>
                <input
                  type="number"
                  id="edit-yearsOfExperience"
                  min="0"
                  step="0.5"
                  value={formData.yearsOfExperience}
                  onChange={(e) => setFormData({ ...formData, yearsOfExperience: e.target.value })}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="edit-lastUsed">Last Used</label>
                <input
                  type="date"
                  id="edit-lastUsed"
                  value={formData.lastUsed}
                  onChange={(e) => setFormData({ ...formData, lastUsed: e.target.value })}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="edit-personalNote">Personal Note</label>
                <textarea
                  id="edit-personalNote"
                  value={formData.personalNote}
                  onChange={(e) => setFormData({ ...formData, personalNote: e.target.value })}
                  className="form-input"
                  rows="3"
                />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserProfile;
